import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import { AppIconButton } from '../components/ui';
import {
  ExpandMore as ExpandMoreIcon,
  Store as StoreIcon,
  Restaurant as RestaurantIcon,
  TableBar as TableBarIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { BuildingOffice2Icon as BuildingOfficeIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { getVendorTableMetrics } from '../api/api';
import DateFilterDropdown from '../components/DateFilterDropdown';
import UnifiedFilterDropdown from '../components/UnifiedFilterDropdown';
import { formatCurrency } from '../config/currency';
import ImprovedVendorOrderFormModal from '../components/ImprovedVendorOrderFormModal';
import InvoiceModal from '../components/InvoiceModal';
import toast from 'react-hot-toast';
import api from '../api/api';
import { useVendorConfig } from '../context/VendorConfigContext';
import { printToLocalPrinter, InvoiceData } from '../services/localPrintService';

interface OrderSummary {
  id: number;
  customOrderId: string;
  status: string;
  orderType: string;
  total: number;
  createdAt: string;
  userName: string;
}

interface TableMetrics {
  tableId: string;
  tableName: string;
  displayNumber?: string;
  qrCode?: string;
  orderCount: number;
  totalAmount: number;
  isDeleted: boolean;
  orders: OrderSummary[];
}

interface SectionMetrics {
  sectionId: string;
  sectionName: string;
  orderCount: number;
  totalAmount: number;
  isDeleted: boolean;
  tables: TableMetrics[];
}

interface FloorMetrics {
  floorId: string;
  floorName: string;
  floorNumber: number;
  orderCount: number;
  totalAmount: number;
  isDeleted: boolean;
  sections: SectionMetrics[];
}

interface SummaryMetrics {
  totalFloors: number;
  totalOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
}

interface VendorTableMetricsResponse {
  summary: SummaryMetrics;
  floors: FloorMetrics[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const VendorTableOrdersView: React.FC = () => {
  const theme = useTheme();
  const { configuration } = useVendorConfig();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateFilter, setDateFilter] = useState<string>('today');
  const [orderTypeFilters, setOrderTypeFilters] = useState<string[]>([]);
  const [metricsData, setMetricsData] = useState<VendorTableMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
  const [selectedTable, setSelectedTable] = useState<TableMetrics | null>(null);
  const [orderTabValue, setOrderTabValue] = useState(0);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderTableId, setOrderTableId] = useState<string | null>(null);
  const [orderTableName, setOrderTableName] = useState<string | null>(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedOrderForInvoice, setSelectedOrderForInvoice] = useState<number | null>(null);
  const [settlingTableId, setSettlingTableId] = useState<string | null>(null);
  const isSettlingBill = settlingTableId !== null;
  const [autoOpenTableQrCode, setAutoOpenTableQrCode] = useState<string | null>(null);
  // Inline (persistent) money-action errors — enterprise-ui item 6
  const [settleErrors, setSettleErrors] = useState<Record<string, string>>({});
  const [orderActionInFlight, setOrderActionInFlight] = useState<{ orderId: number; action: 'serve' | 'cancel' } | null>(null);
  const [orderErrors, setOrderErrors] = useState<Record<number, string>>({});

  // Shared a11y/touch affordances for the money-action buttons (items 4/9)
  const actionButtonSx = {
    minHeight: 44,
    '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
  } as const;

  const clearSettleError = (tableId: string) =>
    setSettleErrors(prev => {
      if (!(tableId in prev)) return prev;
      const next = { ...prev };
      delete next[tableId];
      return next;
    });

  const clearOrderError = (orderId: number) =>
    setOrderErrors(prev => {
      if (!(orderId in prev)) return prev;
      const next = { ...prev };
      delete next[orderId];
      return next;
    });

  // Check for table query parameter on mount
  useEffect(() => {
    const tableQrCode = searchParams.get('table');
    if (tableQrCode) {
      setAutoOpenTableQrCode(tableQrCode);
      // Clear the query parameter from URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Auto-open table modal when metricsData loads and we have a table to open
  useEffect(() => {
    if (autoOpenTableQrCode && metricsData && !loading) {
      // Find the table with matching qrCode
      for (const floor of metricsData.floors) {
        for (const section of floor.sections) {
          const matchingTable = section.tables.find(
            (table) => table.qrCode === autoOpenTableQrCode
          );
          if (matchingTable) {
            setSelectedTable(matchingTable);
            setAutoOpenTableQrCode(null); // Clear to prevent re-opening
            return;
          }
        }
      }
      // If table not found, clear the autoOpen state
      setAutoOpenTableQrCode(null);
    }
  }, [autoOpenTableQrCode, metricsData, loading]);

  const fetchMetrics = useCallback(async (silent = false) => {
    console.log(`[VendorTableOrdersView] fetchMetrics called. silent=${silent}`);
    if (!silent) {
      console.log('[VendorTableOrdersView] Setting loading=true');
      setLoading(true);
    }
    setError(null);
    try {
      const data = await getVendorTableMetrics(dateFilter, orderTypeFilters);
      setMetricsData(data);
    } catch (err: any) {
      console.error('Failed to fetch table metrics:', err);
      setError(err.response?.data?.message || 'Failed to load metrics');
    } finally {
      if (!silent) {
        console.log('[VendorTableOrdersView] Setting loading=false');
        setLoading(false);
      }
    }
  }, [dateFilter, orderTypeFilters]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PENDING':
        return theme.palette.warning.main;
      case 'ACCEPTED':
        return theme.palette.info.main;
      case 'PREPARING':
        return theme.palette.info.dark;
      case 'READY':
        return theme.palette.success.light;
      case 'SERVED':
        return theme.palette.success.main;
      case 'COMPLETED':
        return theme.palette.success.dark;
      case 'CANCELLED':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const handleTableClick = (table: TableMetrics) => {
    setSelectedTable(table);
    setOrderTabValue(0); // Default to active orders tab
  };

  const handlePlaceOrder = (table: TableMetrics) => {
    setOrderTableId(table.tableId);
    setOrderTableName(table.tableName);
    setShowOrderForm(true);
  };

  const handleSettleBill = async (table: TableMetrics) => {
    // Double-click-proof: ignore while a settle is already in flight
    if (settlingTableId !== null) return;
    try {
      setSettlingTableId(table.tableId);
      clearSettleError(table.tableId); // retry clears the previous inline error
      const activeOrders = table.orders.filter((order) =>
        ['PENDING', 'ACCEPTED', 'PREPARING', 'READY', 'SERVED'].includes(order.status)
      );

      if (activeOrders.length === 0) {
        toast.error('No active orders to settle');
        setSettleErrors(prev => ({ ...prev, [table.tableId]: 'No active orders to settle' }));
        return;
      }

      const orderIds = activeOrders.map((order) => order.id);
      const settleResponse = await api.post('/orders/settle-bill', { orderIds });

      const settledCount = settleResponse.data.settledCount || 0;
      if (settledCount === 0) {
        toast.error('Failed to complete any orders');
        setSettleErrors(prev => ({ ...prev, [table.tableId]: 'Failed to settle — no orders were completed. Try again.' }));
        return;
      }

      // Optimistic update: Clear orders for the settled table immediately
      if (metricsData) {
        const updatedFloors = metricsData.floors.map(floor => ({
          ...floor,
          sections: floor.sections.map(section => ({
            ...section,
            tables: section.tables.map(t => {
              if (t.tableId === table.tableId) {
                return {
                  ...t,
                  orderCount: 0,
                  totalAmount: 0,
                  orders: []
                };
              }
              return t;
            })
          }))
        }));

        setMetricsData({
          ...metricsData,
          floors: updatedFloors
        });
      }

      // Refresh metrics silently in background to ensure consistency
      fetchMetrics(true);

      // Show invoice
      const primaryOrderId = settleResponse.data.primaryOrderId;
      if (primaryOrderId) {
        setSelectedOrderForInvoice(primaryOrderId);
        setShowInvoiceModal(true);

        // Auto-print invoice if setting is enabled
        if (configuration?.autoPrintInvoiceOnSettle) {
          // Fetch and print in background - don't block UI
          api.get(`/invoices/by-order/${primaryOrderId}`)
            .then((invoiceResponse) => {
              const invoiceData = invoiceResponse.data as InvoiceData;
              if (invoiceData) {
                printToLocalPrinter(invoiceData)
                  .then((result) => {
                    if (result.success) {
                      console.log('[AutoPrint] Invoice printed successfully');
                    } else {
                      console.warn('[AutoPrint] Invoice print failed:', result.error);
                    }
                  })
                  .catch((err) => console.error('[AutoPrint] Invoice print error:', err));
              }
            })
            .catch((err) => console.error('[AutoPrint] Failed to fetch invoice:', err));
        }
      }

      // Close table details
      setSelectedTable(null);
    } catch (error: any) {
      console.error('Error settling bill:', error);
      const message = error?.response?.data?.message || 'Failed to settle bill';
      toast.error(message);
      // Persist the error inline next to the Settle control (cleared on retry/success)
      setSettleErrors(prev => ({ ...prev, [table.tableId]: message }));
    } finally {
      setSettlingTableId(null);
    }
  };

  const handleViewBill = (table: TableMetrics) => {
    const firstOrder = table.orders[0];
    if (firstOrder) {
      setSelectedOrderForInvoice(firstOrder.id);
      setShowInvoiceModal(true);
    }
  };

  const handleCancelOrder = async (orderId: number) => {
    // Double-click-proof: one order action at a time
    if (orderActionInFlight) return;
    setOrderActionInFlight({ orderId, action: 'cancel' });
    clearOrderError(orderId); // retry clears the previous inline error
    try {
      await api.post(`/orders/${orderId}/cancel`);
      toast.success('Order cancelled successfully');
      await fetchMetrics(true);
      setSelectedTable(null);
    } catch (error: any) {
      console.error('Error cancelling order:', error);
      const message = error?.response?.data?.message || 'Failed to cancel order';
      toast.error(message);
      setOrderErrors(prev => ({ ...prev, [orderId]: message }));
    } finally {
      setOrderActionInFlight(null);
    }
  };

  const handleServeOrder = async (orderId: number) => {
    // Double-click-proof: one order action at a time
    if (orderActionInFlight) return;
    setOrderActionInFlight({ orderId, action: 'serve' });
    clearOrderError(orderId); // retry clears the previous inline error
    try {
      await api.post(`/orders/${orderId}/serve`);
      toast.success('Order marked as served');
      await fetchMetrics(true);
      setSelectedTable(null);
    } catch (error: any) {
      console.error('Error serving order:', error);
      const message = error?.response?.data?.message || 'Failed to mark order as served';
      toast.error(message);
      setOrderErrors(prev => ({ ...prev, [orderId]: message }));
    } finally {
      setOrderActionInFlight(null);
    }
  };

  // Filter floors based on selected floor
  const filteredFloors = selectedFloorId
    ? metricsData?.floors.filter((floor) => floor.floorId === selectedFloorId) || []
    : metricsData?.floors || [];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1400px', mx: 'auto' }}>
      {/* Header */}
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        📊 Orders by Table
      </Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <DateFilterDropdown
          value={dateFilter}
          onChange={setDateFilter}
          showAllOption={true}
        />
        <UnifiedFilterDropdown
          orderTypeFilters={orderTypeFilters}
          dateFilter={dateFilter}
          onOrderTypeChange={setOrderTypeFilters}
          onDateFilterChange={setDateFilter}
        />
      </Box>

      {/* Floor Filter Pills */}
      {metricsData && metricsData.floors.length > 0 && (
        <Box
          sx={{
            px: { xs: 0, sm: 0 },
            pt: 2,
            pb: 2.5,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: 'var(--bg-surface)',
            borderBottom: '2px solid var(--border-subtle)',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            <Chip
              label="All Floors"
              onClick={() => setSelectedFloorId(null)}
              size="medium"
              icon={<BuildingOfficeIcon style={{ width: 16, height: 16 }} />}
              sx={{
                backgroundColor: selectedFloorId === null ? '#3b82f633' : '#f8fafc',
                color: selectedFloorId === null ? '#2563eb' : '#64748b',
                border: selectedFloorId === null ? '1.5px solid #3b82f650' : '1px solid #e2e8f0',
                fontWeight: selectedFloorId === null ? 700 : 600,
                height: 32,
                fontSize: '0.813rem',
                '& .MuiChip-label': { px: 1.5 },
                '&:hover': {
                  backgroundColor: selectedFloorId === null ? '#3b82f640' : '#f1f5f9',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                },
                transition: 'all 0.2s ease',
              }}
            />
            {metricsData.floors.map((floor, index) => {
              const selected = selectedFloorId === floor.floorId;
              const colors = [
                { bg: '#10b98133', color: '#059669', border: '#10b98150' }, // Green
                { bg: '#f59e0b33', color: '#d97706', border: '#f59e0b50' }, // Orange
                { bg: '#8b5cf633', color: '#7c3aed', border: '#8b5cf650' }, // Purple
                { bg: '#ec489933', color: '#db2777', border: '#ec489950' }, // Pink
                { bg: '#06b6d433', color: '#0891b2', border: '#06b6d450' }, // Cyan
              ];
              const colorScheme = colors[index % colors.length];

              return (
                <Chip
                  key={floor.floorId}
                  label={`Floor ${floor.floorNumber}`}
                  onClick={() => setSelectedFloorId(selected ? null : floor.floorId)}
                  size="medium"
                  icon={<BuildingOfficeIcon style={{ width: 16, height: 16 }} />}
                  sx={{
                    backgroundColor: selected ? colorScheme.bg : '#f8fafc',
                    color: selected ? colorScheme.color : 'var(--text-tertiary)',
                    border: selected ? `1.5px solid ${colorScheme.border}` : '1px solid #e2e8f0',
                    fontWeight: selected ? 700 : 600,
                    height: 32,
                    fontSize: '0.813rem',
                    '& .MuiChip-label': { px: 1.5 },
                    '&:hover': {
                      backgroundColor: selected ? colorScheme.bg : '#f1f5f9',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {/* Summary Cards */}
      {metricsData?.summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <ReceiptIcon color="primary" fontSize="small" />
                  <Typography variant="caption" color="textSecondary">
                    Total Orders
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={600}>
                  {metricsData.summary.totalOrders}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <TrendingUpIcon color="success" fontSize="small" />
                  <Typography variant="caption" color="textSecondary">
                    Total Revenue
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={600} color="success.main">
                  {formatCurrency(metricsData.summary.totalRevenue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <ReceiptIcon color="info" fontSize="small" />
                  <Typography variant="caption" color="textSecondary">
                    Avg Order Value
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={600}>
                  {formatCurrency(metricsData.summary.averageOrderValue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <StoreIcon color="secondary" fontSize="small" />
                  <Typography variant="caption" color="textSecondary">
                    Active Floors
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={600}>
                  {metricsData.summary.totalFloors}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Floor Hierarchy */}
      {filteredFloors.length > 0 ? (
        <Box>
          {filteredFloors.map((floor) => (
            <Accordion
              key={floor.floorId}
              defaultExpanded={true}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <StoreIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    {floor.floorName}
                  </Typography>
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    <Chip
                      label={`${floor.orderCount} orders`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={formatCurrency(floor.totalAmount)}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                {/* Sections */}
                {floor.sections.map((section) => (
                  <Accordion key={section.sectionId} sx={{ mb: 1 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: alpha(theme.palette.secondary.main, 0.03),
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2} width="100%">
                        <RestaurantIcon color="secondary" fontSize="small" />
                        <Typography variant="subtitle1" fontWeight={500}>
                          {section.sectionName}
                        </Typography>
                        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                          <Chip
                            label={`${section.orderCount} orders`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={formatCurrency(section.totalAmount)}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                      {/* Tables */}
                      <Grid container spacing={2}>
                        {section.tables.map((table) => (
                          <Grid item xs={12} sm={6} md={4} key={table.tableId}>
                            <Paper
                              elevation={2}
                              onClick={() => handleTableClick(table)}
                              sx={{
                                p: 2,
                                cursor: 'pointer',
                                borderLeft: `4px solid ${table.orderCount > 0
                                  ? theme.palette.success.main
                                  : theme.palette.grey[300]
                                  }`,
                                transition: 'all 0.2s',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: 4,
                                },
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <TableBarIcon fontSize="small" color="action" />
                                <Typography variant="body1" fontWeight={600}>
                                  Table {table.displayNumber || table.tableName}
                                </Typography>
                              </Box>

                              <Typography variant="body2" color="textSecondary" gutterBottom>
                                {table.orderCount} orders · {formatCurrency(table.totalAmount)}
                              </Typography>

                              {/* Quick Actions */}
                              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePlaceOrder(table);
                                  }}
                                  sx={{ fontSize: '0.75rem' }}
                                >
                                  Place Order
                                </Button>
                                {table.orderCount > 0 && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    color="success"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSettleBill(table);
                                    }}
                                    disabled={isSettlingBill}
                                    aria-busy={settlingTableId === table.tableId || undefined}
                                    startIcon={settlingTableId === table.tableId ? <CircularProgress size={14} color="inherit" /> : undefined}
                                    sx={{ fontSize: '0.75rem', ...actionButtonSx }}
                                  >
                                    {settlingTableId === table.tableId ? 'Settling…' : 'Settle'}
                                  </Button>
                                )}
                              </Box>

                              {/* Inline settle error — persists until retry/success */}
                              {settleErrors[table.tableId] && (
                                <Typography
                                  role="alert"
                                  variant="caption"
                                  sx={{ display: 'block', mt: 1, fontWeight: 500, color: 'var(--status-bad)' }}
                                >
                                  {settleErrors[table.tableId]}
                                </Typography>
                              )}
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : (
        <Alert severity="info">
          No orders found for the selected filters. Try changing the date range or order types.
        </Alert>
      )}

      {/* Table Details Dialog */}
      {selectedTable && (
        <Dialog
          open={true}
          onClose={() => setSelectedTable(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: '80vh',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Table {selectedTable.displayNumber || selectedTable.tableName}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedTable.orderCount} orders · {formatCurrency(selectedTable.totalAmount)}
              </Typography>
            </Box>
            <AppIconButton
              icon={<CloseIcon />}
              tooltip="Close"
              variant="ghost"
              size="small"
              onClick={() => setSelectedTable(null)}
            />
          </DialogTitle>

          <DialogContent sx={{ p: 0 }}>
            <Tabs
              value={orderTabValue}
              onChange={(_, newValue) => setOrderTabValue(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            >
              <Tab label={`Active (${selectedTable.orders.length})`} />
            </Tabs>

            <TabPanel value={orderTabValue} index={0}>
              {selectedTable.orders.length > 0 ? (
                <Box sx={{ px: 2, pb: 2 }}>
                  {selectedTable.orders.map((order) => (
                    <Paper
                      key={order.id}
                      variant="outlined"
                      sx={{ p: 2, mb: 2 }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="start">
                        <Box flex={1}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {order.customOrderId}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {order.userName}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {order.orderType}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Chip
                            label={order.status}
                            size="small"
                            sx={{
                              backgroundColor: alpha(getStatusColor(order.status), 0.15),
                              color: getStatusColor(order.status),
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              mb: 1,
                            }}
                          />
                          <Typography variant="h6" fontWeight={600}>
                            {formatCurrency(order.total)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Order Actions */}
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        {order.status !== 'SERVED' && order.status !== 'COMPLETED' && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            onClick={() => handleServeOrder(order.id)}
                            disabled={orderActionInFlight !== null}
                            aria-busy={(orderActionInFlight?.orderId === order.id && orderActionInFlight.action === 'serve') || undefined}
                            startIcon={orderActionInFlight?.orderId === order.id && orderActionInFlight.action === 'serve' ? <CircularProgress size={14} color="inherit" /> : undefined}
                            sx={{ fontSize: '0.75rem', ...actionButtonSx }}
                          >
                            {orderActionInFlight?.orderId === order.id && orderActionInFlight.action === 'serve' ? 'Serving…' : 'Mark Served'}
                          </Button>
                        )}
                        {order.status !== 'CANCELLED' && order.status !== 'COMPLETED' && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={() => handleCancelOrder(order.id)}
                            disabled={orderActionInFlight !== null}
                            aria-busy={(orderActionInFlight?.orderId === order.id && orderActionInFlight.action === 'cancel') || undefined}
                            startIcon={orderActionInFlight?.orderId === order.id && orderActionInFlight.action === 'cancel' ? <CircularProgress size={14} color="inherit" /> : undefined}
                            sx={{ fontSize: '0.75rem', ...actionButtonSx }}
                          >
                            {orderActionInFlight?.orderId === order.id && orderActionInFlight.action === 'cancel' ? 'Cancelling…' : 'Cancel'}
                          </Button>
                        )}
                      </Box>

                      {/* Inline order-action error — persists until retry/success */}
                      {orderErrors[order.id] && (
                        <Typography
                          role="alert"
                          variant="caption"
                          sx={{ display: 'block', mt: 1, fontWeight: 500, color: 'var(--status-bad)' }}
                        >
                          {orderErrors[order.id]}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    No active orders
                  </Typography>
                </Box>
              )}
            </TabPanel>
          </DialogContent>

          <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
            {/* Inline settle error — persists next to the Settle control until retry/success */}
            {settleErrors[selectedTable.tableId] && (
              <Typography
                role="alert"
                variant="caption"
                sx={{ mr: 'auto', fontWeight: 500, color: 'var(--status-bad)' }}
              >
                {settleErrors[selectedTable.tableId]}
              </Typography>
            )}
            <Button
              variant="outlined"
              onClick={() => handlePlaceOrder(selectedTable)}
              sx={{ fontSize: '0.8rem' }}
            >
              Place Order
            </Button>
            {selectedTable.orderCount > 0 && (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleViewBill(selectedTable)}
                  sx={{ fontSize: '0.8rem' }}
                >
                  View Bill
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleSettleBill(selectedTable)}
                  disabled={isSettlingBill}
                  aria-busy={isSettlingBill || undefined}
                  startIcon={isSettlingBill ? <CircularProgress size={14} color="inherit" /> : <BanknotesIcon style={{ width: 14, height: 14 }} />}
                  sx={{ fontSize: '0.8rem', ...actionButtonSx }}
                >
                  {isSettlingBill ? 'Settling…' : 'Settle'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}

      {/* Place Order Modal */}
      {showOrderForm && (
        <ImprovedVendorOrderFormModal
          isOpen={showOrderForm}
          onClose={() => {
            setShowOrderForm(false);
            setOrderTableId(null);
            setOrderTableName(null);
          }}
          tableId={orderTableId}
          tableName={orderTableName}
          onOrderPlaced={() => {
            setShowOrderForm(false);
            setOrderTableId(null);
            setOrderTableName(null);
            fetchMetrics(true); // Refresh metrics after order placed
          }}
        />
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && (
        <InvoiceModal
          isOpen={showInvoiceModal}
          onClose={() => {
            setShowInvoiceModal(false);
            setSelectedOrderForInvoice(null);
            fetchMetrics(true); // Refresh metrics after invoice closed
          }}
          orderId={selectedOrderForInvoice}
          orderDisplayId={
            selectedTable?.orders.find((order) => order.id === selectedOrderForInvoice)
              ?.customOrderId
          }
        />
      )}
    </Box>
  );
};

export default VendorTableOrdersView;
